import { createLogger } from "@/lib/logger";
import {
  type LocalParticipant,
  ParticipantKind,
  type RemoteParticipant,
  Room,
  RoomEvent,
} from "livekit-client";
import type { WhiteboardSpec } from "../spec";
import {
  type Artifact,
  ArtifactSchema,
  CAPABILITY,
  CODE_TOPIC,
  EVENT_TOPIC,
  LEGACY_CODE_TOPIC,
  LEGACY_EVENT_TOPIC,
  MAX_ARTIFACT_BYTES,
  SDK_VERSION,
} from "./contract";
import { type CodeEvent, ExerciseController, controllerKey, controllers } from "./controller";
import { prewarmCodeRuntime } from "./prewarm";

const logger = createLogger("GeneratedExerciseTransport");
export function connectGeneratedRoom(
  room: Room,
  show: (spec: WhiteboardSpec) => void,
  observe?: (event: CodeEvent) => void
) {
  let closed = false;
  let generation = 0;
  let minimumRequest = 0;
  let current: ExerciseController | null = null;
  let sender: string | undefined;
  let session: string | undefined;
  let eventTopic = EVENT_TOPIC;
  const pending = new Set<ExerciseController>();
  const readers = new Set<AbortController>();
  const send = (event: CodeEvent) => {
    if (closed || !sender) return;
    observe?.(event);
    void room.localParticipant
      .publishData(new TextEncoder().encode(JSON.stringify(event)), {
        reliable: true,
        topic: eventTopic,
        destinationIdentities: [sender],
      })
      .catch(() => logger.warn("Exercise event delivery failed", { type: event.type }));
  };
  const trusted = (identity: string) => {
    const p = room.remoteParticipants.get(identity);
    return (
      p?.kind === ParticipantKind.AGENT &&
      ["main3live", "main3geminicode", "main3geminiboard"].includes(
        p.attributes["chickytutor.runtime"]
      )
    );
  };
  // Verified against https://docs.livekit.io/transport/data/byte-streams/
  const register = (topic: string) =>
    room.registerByteStreamHandler(topic, async (reader, info) => {
      if (closed || !trusted(info.identity) || (sender && sender !== info.identity)) return;
      const epoch = ++generation;
      readers.forEach((r) => r.abort());
      const abort = new AbortController();
      readers.add(abort);
      reader.signal = abort.signal;
      const timeout = setTimeout(() => abort.abort(), 10000);
      let length = 0;
      const chunks: Uint8Array[] = [];
      try {
        if ((reader.info.size ?? 0) > MAX_ARTIFACT_BYTES) throw new Error("Artifact too large");
        for await (const chunk of reader) {
          length += chunk.length;
          if (length > MAX_ARTIFACT_BYTES || closed || epoch !== generation)
            throw new Error("Invalid stream");
          chunks.push(chunk);
        }
        const bytes = new Uint8Array(length);
        let offset = 0;
        for (const c of chunks) {
          bytes.set(c, offset);
          offset += c.length;
        }
        const artifact = ArtifactSchema.parse(
          JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes))
        );
        if ((artifact.sdk === "2") !== (topic === CODE_TOPIC))
          throw new Error("SDK topic mismatch");
        const digest = await crypto.subtle.digest(
          "SHA-256",
          new TextEncoder().encode(artifact.program.source)
        );
        const hash = [...new Uint8Array(digest)]
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("");
        if (hash !== artifact.hash || (session && session !== artifact.session))
          throw new Error("Artifact mismatch");
        if (closed || epoch !== generation) return;
        if (artifact.request < minimumRequest) throw new Error("Superseded request");
        sender = info.identity;
        eventTopic = artifact.sdk === "2" ? EVENT_TOPIC : LEGACY_EVENT_TOPIC;
        session = artifact.session;
        const previous = current?.snapshot.artifact;
        if (previous && artifact.request < previous.request) throw new Error("Superseded request");
        if (previous?.id === artifact.id) {
          if (artifact.revision === previous.revision) {
            if (artifact.sdk === "1") current?.mounted(true);
            else current?.resume();
            return;
          }
          if (artifact.parentRevision !== previous.revision)
            throw new Error("Stale parent revision");
        } else if (artifact.parentRevision !== 0) throw new Error("Missing parent exercise");
        const candidate =
          previous?.id === artifact.id ? current! : new ExerciseController(artifact, send);
        pending.add(candidate);
        const ready = await candidate.load(artifact);
        pending.delete(candidate);
        if (closed || epoch !== generation) {
          if (candidate !== current) candidate.close();
          return;
        }
        if (!ready) return;
        if (current !== candidate) {
          if (current) {
            controllers.delete(controllerKey(current.snapshot.artifact));
            current.close();
          }
          current = candidate;
        }
        controllers.set(controllerKey(artifact), candidate);
        show(referenceSpec(artifact));
      } catch (error) {
        logger.warn("Executable artifact rejected", { reason: String(error).slice(0, 120) });
      } finally {
        clearTimeout(timeout);
        readers.delete(abort);
      }
    });
  register(LEGACY_CODE_TOPIC);
  register(CODE_TOPIC);
  const data = (
    bytes: Uint8Array,
    participant?: RemoteParticipant,
    _kind?: unknown,
    topic?: string
  ) => {
    if (topic === "whiteboard" && participant && trusted(participant.identity)) {
      try {
        const event = JSON.parse(new TextDecoder().decode(bytes));
        if (
          event.type === "whiteboard_show" ||
          (event.type === "whiteboard_clear" &&
            (!event.exerciseId || event.exerciseId === current?.snapshot.artifact.id))
        ) {
          generation++;
          readers.forEach((r) => r.abort());
          pending.forEach((c) => c.cancelPending());
          if (current) {
            controllers.delete(controllerKey(current.snapshot.artifact));
            current.close();
            current = null;
          }
        }
      } catch {
        /* Other data is not a command. */
      }
    }
    if (
      (topic !== EVENT_TOPIC && topic !== LEGACY_EVENT_TOPIC) ||
      !participant ||
      !trusted(participant.identity) ||
      (sender && participant.identity !== sender)
    )
      return;
    try {
      const event = JSON.parse(new TextDecoder().decode(bytes));
      if (event.type === "disable") {
        generation++;
        pending.forEach((c) => c.cancelPending());
        current?.fallback("Code mode disabled", false);
      } else if (
        event.type === "invalidate" &&
        Number.isSafeInteger(event.minimumRequest) &&
        event.minimumRequest >= minimumRequest
      ) {
        minimumRequest = event.minimumRequest;
        generation++;
        readers.forEach((r) => r.abort());
        pending.forEach((c) => c.cancelPending());
      }
    } catch {
      /* Invalid lifecycle packet. */
    }
  };
  const advertise = () => {
    const native = (
      globalThis as typeof globalThis & { Capacitor?: { isNativePlatform?: () => boolean } }
    ).Capacitor;
    if (native?.isNativePlatform?.()) return;
    if (typeof Worker === "undefined" || typeof WebAssembly === "undefined" || !crypto.subtle)
      return;
    if (
      [...room.remoteParticipants.values()].some(
        (p) =>
          p.kind === ParticipantKind.AGENT && p.attributes["chickytutor.exercise.mode"] === "code"
      )
    )
      void prewarmCodeRuntime();
    void room.localParticipant.setAttributes({ [CAPABILITY]: SDK_VERSION }).catch(() => undefined);
    if (current && sender) current.resume();
  };
  room.on(RoomEvent.DataReceived, data);
  room.on(RoomEvent.Connected, advertise);
  room.on(RoomEvent.Reconnected, advertise);
  const changed = (
    _attributes: Record<string, string>,
    participant: RemoteParticipant | LocalParticipant
  ) => {
    if (
      participant.kind === ParticipantKind.AGENT &&
      participant.attributes["chickytutor.exercise.mode"] === "code"
    )
      void prewarmCodeRuntime();
  };
  room.on(RoomEvent.ParticipantAttributesChanged, changed);
  advertise();
  return () => {
    closed = true;
    generation++;
    readers.forEach((r) => r.abort());
    readers.clear();
    room.unregisterByteStreamHandler(CODE_TOPIC);
    room.unregisterByteStreamHandler(LEGACY_CODE_TOPIC);
    room.off(RoomEvent.DataReceived, data);
    room.off(RoomEvent.Connected, advertise);
    room.off(RoomEvent.Reconnected, advertise);
    room.off(RoomEvent.ParticipantAttributesChanged, changed);
    if (current) {
      controllers.delete(controllerKey(current.snapshot.artifact));
      current.close();
    }
    pending.forEach((c) => c.close());
  };
}
export function referenceSpec(a: Artifact): WhiteboardSpec {
  return {
    kind: "composed",
    id: a.id,
    title: a.program.title,
    blocks: [
      { type: "text", content: a.program.objective },
      ...(a.program.passage
        ? [{ type: "text" as const, content: a.program.passage.slice(0, 4000) }]
        : []),
      {
        type: "text",
        content: a.program.activities
          .map((v) => v.prompt)
          .join("\n\n")
          .slice(0, 4000),
      },
    ],
    generated: { session: a.session, id: a.id, revision: a.revision },
  };
}
